/**
 * editor2/modals/PaymentEmailModal.tsx
 * 支払い確認メール送信モーダル
 * Step 1: 言語選択 (ja/en/tw/kr)
 * Step 2: プレビュー表示 (to/subject/body) → 送信
 */
import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import type { EditorReservation } from '../types';
import type { AiEmailMeta, AiEmailVersion, AiChatEntry } from '../aiEmail';
import type { GeminiContent } from '../../../agent/types';

export interface PaymentEmailState {
  open: boolean;
  step: 'lang' | 'ai_compose' | 'preview';
  type: string; // 'payment' | 'free' | 'request_ok' | 'request_ok_night' | 'request_full' | 'request_offer' | 'cancel' | 'cancel_fee' | 'comment' | 'ai_compose'
  lang: string; // 'ja' | 'en' | 'tw' | 'kr' — AI改善時に必要
  to: string;
  /** CC（カンマ区切りで複数可）。普段は使わないため UI は折りたたみ */
  cc: string;
  /** BCC（カンマ区切りで複数可） */
  bcc: string;
  /** CC/BCC 入力欄の開閉状態 */
  showCcBcc: boolean;
  subject: string;
  body: string;
  sending: boolean;
  error: string;
  aiInstruction: string; // 改善指示（プレビュー画面のAI改善で使う）
  aiImproving: boolean;
  composePrompt: string; // 生成指示（ai_compose ステップで使う、AIで作成）
  aiComposing: boolean;
  // --- AI 判断メタ + 対話履歴（Track C, 2026-07-04） ---
  /** 最新生成の判断理由・前提・確認事項（AIのコメントパネルに表示） */
  aiMeta: AiEmailMeta | null;
  /** 生成/改善のバージョン履歴（差し戻し用） */
  aiHistory: AiEmailVersion[];
  /** マルチターン会話履歴（改善指示が文脈を保って効く） */
  aiConversation: GeminiContent[];
  /** 対話スレッド表示用（指示と AI 応答の吹き出し。2026-07-09） */
  aiChat: AiChatEntry[];
}

export const INITIAL_PAYMENT_EMAIL_STATE: PaymentEmailState = {
  open: false,
  step: 'lang',
  type: 'payment',
  lang: 'ja',
  to: '',
  cc: '',
  bcc: '',
  showCcBcc: false,
  subject: '',
  body: '',
  sending: false,
  error: '',
  aiInstruction: '',
  aiImproving: false,
  composePrompt: '',
  aiComposing: false,
  aiMeta: null,
  aiHistory: [],
  aiConversation: [],
  aiChat: [],
};

interface PaymentEmailModalProps {
  open: boolean;
  editing: EditorReservation;
  sysApiBase: string;
  onClose: () => void;
  /**
   * 送信ハンドラ。templateType は送信後の予約メモ記録用（本文には使わない）。
   * ⚠️ 送信ボタンはこの onSend を直接呼ぶ（usePaymentEmail.handleSend は経由しない）。
   *    ここに項目を足し忘れると、state にあっても送信されないので注意。
   */
  onSend: (emailData: {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    body: string;
    templateType?: string;
  }) => void;
  // State management delegated to parent/hook
  state: PaymentEmailState;
  onStateChange: (updater: (prev: PaymentEmailState) => PaymentEmailState) => void;
  onLangSelect: (lang: string) => void;
  onAiImprove: () => void; // 改善指示 + 現在の本文を Gemini に送り、結果で本文を置換
  onAiCompose: () => void; // composePrompt + 予約データ + 基本ルールを Gemini に送り、メール本文を新規生成
  onRestoreVersion?: (versionId: string) => void; // 生成履歴からの差し戻し
}

function getEmailTypeTitle(type: string, step: string): string {
  if (step === 'preview') return 'メールプレビュー';
  if (step === 'ai_compose') return '✨ AIでメール本文を作成';
  switch (type) {
    case 'payment':
      return '支払い確認メールを作成しますか？';
    case 'request_ok':
      return 'リクエスト承認メールを作成しますか？';
    case 'request_ok_night':
      return 'リクエスト（深夜早朝）承認メールを作成しますか？';
    case 'request_full':
      return 'リクエスト満車回答メールを作成しますか？';
    case 'request_offer':
      return 'リクエスト代替案メールを作成しますか？';
    case 'cancel':
      return 'キャンセル連絡メールを作成しますか？';
    case 'cancel_fee':
      return 'キャンセル料連絡メールを作成しますか？';
    case 'comment':
      return 'コメント返信メールを作成しますか？';
    default:
      return 'フリーメールを作成しますか？';
  }
}

/** 対話スレッドの吹き出し1件（Track C2 対話型化, 2026-07-09） */
function AiChatBubble({ entry, versionNo }: { entry: AiChatEntry; versionNo?: number }) {
  if (entry.role === 'user') {
    return (
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%', bgcolor: '#6366f1', color: '#fff', px: 1.25, py: 0.75, borderRadius: '10px 10px 2px 10px' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry.text}</Typography>
      </Box>
    );
  }
  const toolCounts = (entry.toolLog || []).reduce<Record<string, number>>((acc, t) => {
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '90%', bgcolor: '#fff', border: '1px solid #e2e8f0', px: 1.25, py: 0.75, borderRadius: '10px 10px 10px 2px' }}>
      {entry.kind === 'draft-update' && (
        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'block' }}>
          ✏️ 本文を更新しました{versionNo ? `（v${versionNo}）` : ''}
        </Typography>
      )}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry.text}</Typography>
      {(entry.assumptions?.length || 0) > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block' }}>📌 置いた前提</Typography>
          {entry.assumptions!.map((a, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', pl: 1 }}>・{a}</Typography>
          ))}
        </Box>
      )}
      {(entry.checkpoints?.length || 0) > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#b91c1c', display: 'block' }}>⚠️ 送信前に確認</Typography>
          {entry.checkpoints!.map((c, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', pl: 1 }}>・{c}</Typography>
          ))}
        </Box>
      )}
      {Object.keys(toolCounts).length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          実データ確認: {Object.entries(toolCounts).map(([t, n]) => (n > 1 ? `${t} ×${n}` : t)).join(', ')}
        </Typography>
      )}
    </Box>
  );
}

export default function PaymentEmailModal({
  open,
  state,
  onClose,
  onSend,
  onStateChange,
  onLangSelect,
  onAiImprove,
  onAiCompose,
  onRestoreVersion,
}: PaymentEmailModalProps) {
  const title = getEmailTypeTitle(state.type, state.step);

  // 対話スレッドの自動スクロール（新しい吹き出し・考え中表示のたびに最下部へ）
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.aiChat.length, state.aiImproving]);

  // バージョンID → v番号（吹き出しの「本文を更新しました（v2）」表示用）
  const versionNoById = new Map(state.aiHistory.map((v, i) => [v.id, i + 1]));

  const canChat = !state.aiImproving && !state.sending && !!state.body.trim();
  const sendChat = () => {
    if (canChat && state.aiInstruction.trim()) onAiImprove();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {state.step === 'lang' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('ja')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              日本語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('en')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              英語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('tw')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              中国語（繁体字）
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('kr')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              韓国語
            </Button>
          </Box>
        ) : state.step === 'ai_compose' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              どんなメールを作りますか？ 言語を指定したい場合は「英文で」「韓国語で」など指示文に含めてください。
              予約データ（顧客名・予約コード・日時・料金等）と基本ルール（キャンセルポリシー等）は自動で AI に渡されます。
            </Typography>
            <TextField
              label="生成指示"
              fullWidth
              multiline
              rows={6}
              autoFocus
              placeholder={'例)\n・ 直前のキャンセルに対するお詫びと、キャンセル料の案内を英文で\n・ 来店時間変更の確認を簡潔に\n・ 雨天の予報があるので、車両受け取り時の注意喚起を一言'}
              value={state.composePrompt}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, composePrompt: e.target.value }))
              }
              disabled={state.aiComposing}
            />
            {state.error && (
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {state.aiHistory.length > 1 && onRestoreVersion && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">履歴:</Typography>
                {state.aiHistory.map((v, i) => (
                  <Tooltip key={v.id} title={v.instruction || '(指示なし)'}>
                    <Chip
                      size="small"
                      label={`v${i + 1}`}
                      variant={i === state.aiHistory.length - 1 ? 'filled' : 'outlined'}
                      color={i === state.aiHistory.length - 1 ? 'secondary' : 'default'}
                      onClick={() => onRestoreVersion(v.id)}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
            <TextField
              label="宛先"
              fullWidth
              size="small"
              value={state.to}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, to: e.target.value }))
              }
            />

            {/* CC / BCC — 普段は使わないので折りたたみ。値が入っている場合は開いたままにする */}
            {!state.showCcBcc && !state.cc && !state.bcc ? (
              <Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onStateChange((prev) => ({ ...prev, showCcBcc: true }))}
                  sx={{ textTransform: 'none', px: 0.5 }}
                >
                  ＋ CC / BCC を追加
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="CC"
                  placeholder="複数指定はカンマ区切り"
                  fullWidth
                  size="small"
                  value={state.cc}
                  onChange={(e) =>
                    onStateChange((prev) => ({ ...prev, cc: e.target.value }))
                  }
                />
                <TextField
                  label="BCC"
                  placeholder="複数指定はカンマ区切り"
                  fullWidth
                  size="small"
                  value={state.bcc}
                  onChange={(e) =>
                    onStateChange((prev) => ({ ...prev, bcc: e.target.value }))
                  }
                />
                <Box>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      onStateChange((prev) => ({ ...prev, showCcBcc: false, cc: '', bcc: '' }))
                    }
                    sx={{ textTransform: 'none', px: 0.5 }}
                  >
                    CC / BCC を閉じる（入力内容は破棄）
                  </Button>
                </Box>
              </Box>
            )}

            <TextField
              label="件名"
              fullWidth
              size="small"
              value={state.subject}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, subject: e.target.value }))
              }
            />
            <TextField
              label="本文"
              fullWidth
              multiline
              rows={8}
              value={state.body}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, body: e.target.value }))
              }
            />

            {/* AIとの対話スレッド（Track C2 対話型化, 2026-07-09） */}
            <Box sx={{ border: '1px solid #ddd6fe', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#f5f3ff' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d28d9' }}>
                  💬 AIと相談して仕上げる（このやり取りはお客様には送信されません）
                </Typography>
              </Box>
              <Box
                ref={chatScrollRef}
                sx={{ maxHeight: 240, overflowY: 'auto', p: 1, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', gap: 0.75 }}
              >
                {state.aiChat.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                    「もっと短く」「英語にして」「この条件で受けられる？」など、AIに話しかけて仕上げられます。
                    AIから確認の質問が返ってくることもあります。
                  </Typography>
                )}
                {state.aiChat.map((entry) => (
                  <AiChatBubble key={entry.id} entry={entry} versionNo={entry.versionId ? versionNoById.get(entry.versionId) : undefined} />
                ))}
                {state.aiImproving && (
                  <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">考え中…</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid #eee', alignItems: 'flex-end' }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="AIへの指示・質問（Enterで送信 / Shift+Enterで改行）"
                  value={state.aiInstruction}
                  onChange={(e) =>
                    onStateChange((prev) => ({ ...prev, aiInstruction: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  disabled={state.aiImproving || state.sending}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={sendChat}
                  disabled={!canChat || !state.aiInstruction.trim()}
                  sx={{ whiteSpace: 'nowrap', minWidth: 64 }}
                >
                  送信
                </Button>
              </Box>
            </Box>

            {state.error && (
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Button onClick={onClose} color="inherit">
          いいえ
        </Button>
        {state.step === 'ai_compose' && (
          <Button
            onClick={onAiCompose}
            variant="contained"
            disabled={state.aiComposing || !state.composePrompt.trim()}
            startIcon={
              state.aiComposing ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              whiteSpace: 'nowrap',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              },
            }}
          >
            {state.aiComposing ? '生成中…' : '✨ 生成する'}
          </Button>
        )}
        {state.step === 'preview' && (
          <Button
            onClick={() =>
              onSend({
                to: state.to,
                ...(state.cc.trim() ? { cc: state.cc.trim() } : {}),
                ...(state.bcc.trim() ? { bcc: state.bcc.trim() } : {}),
                subject: state.subject,
                body: state.body,
                templateType: state.type,
              })
            }
            variant="contained"
            disabled={state.sending || state.aiImproving}
            startIcon={
              state.sending ? <CircularProgress size={20} color="inherit" /> : undefined
            }
          >
            送信する
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
